import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Target, Heart, Users, Lightbulb, Globe, Shield, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { aboutContent, stats } from '../data/mock';
import SEO from '../components/ui/SEO';

gsap.registerPlugin(ScrollTrigger);

const valueIcons = {
  Integrity: Heart,
  Excellence: Target,
  Partnership: Users,
  Innovation: Lightbulb,
};

const AboutPage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.fromTo('.about-hero',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
      );

      gsap.fromTo('.mission-content',
        { opacity: 0, y: 50 },
        {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
          scrollTrigger: { trigger: '.mission-section', start: 'top 80%' },
        }
      );

      gsap.fromTo('.stat-item',
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1, scale: 1, duration: 0.5, stagger: 0.1, ease: 'power2.out',
          scrollTrigger: { trigger: '.stats-section', start: 'top 85%' },
        }
      );

      gsap.fromTo('.value-card',
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: '.values-grid', start: 'top 80%' },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO
        title="About Us — Your Partner in Business Excellence"
        description="Learn about VISO Connect — a trusted operational support partner helping businesses grow with experienced teams, clear processes, and 24/7 availability."
        canonical="https://visoconnect.in/about"
      />
      <div ref={pageRef} className="overflow-hidden bg-[#0a0a0f]">
        {/* Hero */}
        <section className="pt-32 pb-20 bg-[#0a0a0f] relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-indigo-500/10 rounded-full blur-[150px] pointer-events-none" />

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
            <div className="about-hero text-center">
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                About Us
              </span>
              <h1 className="text-white mb-6" style={{
                fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.03em'
              }}>
                Your Partner in{' '}
                <span className="gradient-text">Business Excellence</span>
              </h1>
              <p className="text-slate-400 mx-auto" style={{ maxWidth: '520px', fontSize: '1.1rem' }}>
                Empowering businesses with reliable operational support and expert teams.
              </p>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="mission-section py-24 bg-[#111118]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="mission-content grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                  Our Mission
                </span>
                <h2 className="text-white mb-6" style={{
                  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}>
                  Enabling Business Success
                </h2>
                <p className="text-slate-400 leading-relaxed" style={{ maxWidth: '480px' }}>
                  {aboutContent.mission}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Target, title: 'Clear Focus', desc: 'Results-driven approach' },
                  { icon: Users, title: 'Partnership', desc: 'Long-term relationships' },
                  { icon: Globe, title: 'Global Reach', desc: 'Supporting businesses everywhere' },
                  { icon: Shield, title: 'Reliability', desc: 'Consistent, secure delivery' },
                ].map((item, i) => (
                  <div key={i} className="card-dark p-5 text-center">
                    <div className="w-11 h-11 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <item.icon className="text-indigo-400" size={22} />
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-slate-500 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="stats-section py-20 bg-gradient-to-r from-indigo-600 to-cyan-600 relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
          <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              {stats.map((stat) => (
                <div key={stat.id} className="stat-item text-center">
                  <div className="stat-number text-white mb-2">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-white/80 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-24 bg-[#0a0a0f]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
              Our Story
            </span>
            <h2 className="text-white mb-6" style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
              Built for Business
            </h2>
            <p className="text-slate-400 leading-relaxed">
              {aboutContent.story}
            </p>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-[#111118]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                Our Values
              </span>
              <h2 className="text-white mb-4" style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
                What Guides Us
              </h2>
            </div>

            <div className="values-grid grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutContent.values.map((value, index) => {
                const IconComponent = valueIcons[value.title] || Target;
                return (
                  <div key={index} className="value-card card-dark p-6 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-indigo-400" size={24} />
                    </div>
                    <h3 className="text-white font-semibold mb-2 text-sm">{value.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-[#0a0a0f] relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-indigo-500/10 rounded-full blur-[150px] pointer-events-none" />
          <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-white mb-5" style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
              Ready to work together?
            </h2>
            <p className="text-slate-400 mb-10" style={{ maxWidth: '420px', margin: '0 auto 2.5rem' }}>
              Let's discuss how we can support your growth.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-glow bg-gradient-to-r from-indigo-500 to-cyan-500 text-white px-8 py-6 text-base font-semibold rounded-xl border-0">
                  Get in Touch
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-base font-medium rounded-xl">
                  Explore Services
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;
